import { diffWords } from "diff";
import { type FC, useMemo } from "react";
import { twMerge } from "tailwind-merge";

interface WordDiffViewProps {
  savedBody: string;
  editedBody: string;
}

interface DiffPart {
  type: "added" | "removed" | "unchanged";
  text: string;
}

function computeWordDiff(savedBody: string, editedBody: string): DiffPart[] {
  const changes = diffWords(savedBody, editedBody);
  return changes.map((change) => ({
    type: change.added ? "added" : change.removed ? "removed" : "unchanged",
    text: change.value,
  }));
}

export const WordDiffView: FC<WordDiffViewProps> = ({
  savedBody,
  editedBody,
}) => {
  const parts = useMemo(
    () => computeWordDiff(savedBody, editedBody),
    [savedBody, editedBody],
  );

  if (savedBody === editedBody) {
    return <div className="text-xs text-muted-foreground">No changes</div>;
  }

  return (
    <div className="text-xs leading-5 break-words whitespace-pre-wrap">
      {parts.map((part, idx) => (
        <span
          key={idx}
          className={twMerge(
            part.type === "added" && "bg-green-100 text-green-800",
            part.type === "removed" &&
              "bg-red-100 text-red-800 line-through decoration-red-700/60",
          )}
        >
          {part.text}
        </span>
      ))}
    </div>
  );
};
